import { memo, useMemo, useEffect } from "react"
import { Container, Heading, Box, HStack } from "@chakra-ui/react"
import { useImage } from "@app/hooks/useImage"
import { useRoutes } from "@app/hooks/useRoutes"
import { useSection } from "@app/hooks/useSection"
import { ArticleCard } from "@app/components/Article/ArticleCard"
import { Navigation } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import { CarouselOverflowWrapper, extraOverflowParentProps } from "@app/components/CarouselOverflowWrapper"
import { CustomButton } from "@app/components/Button"
import { useSanity } from "@app/hooks/useSanity"

const FeaturedArticles: React.FC<GatsbyTypes.SanitySectionFeaturedArticles> = ({
  title,
  category,
  articles: featured,
  analytics,
  bgColor,
  mobileBackgroundColour,
  button,
}) => {
  const { getGatsbyImage } = useImage()
  const { urlResolver } = useRoutes()
  const { handleTrackingClick } = useSection({ analytics })
  const { getSanity, sanityItems } = useSanity()

  useEffect(() => {
    if (!category?.handle?.current) return null

    const filters = `&& count((categories[]->handle.current)[@ match ["*${category?.handle?.current}*"]]) > 0`

    const projection = [
      `{
        _id,
        title,
        url,
        image {
          alt,
          asset -> {
            ...
          }
        },
        categories[][0..0]->{
          title
        },
        handle {
          current
        },
        attributes {
          publishedAt
        }
      }`,
    ]

    const query = [`*[_type == "article" && !(_id in path('drafts.**')) && defined(attributes.publishedAt) ${filters}]`]
      .concat(projection)
      .concat("| order(attributes.publishedAt desc)")
      .concat("[0...6]")
      .join("")

    getSanity(query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category])

  const articles = useMemo(() => sanityItems || featured, [sanityItems, featured])

  const items = useMemo(
    () =>
      articles?.map((article: any) => ({
        ...article,
        key: article?._id,
        title: article?.title,
        link: urlResolver(article),
        image: getGatsbyImage(article?.image),
        author: article?.attributes?.author?.name || "",
        categories:
          article?.categories?.map((category: any) => ({
            handle: category?.handle?.current,
            title: category?.title,
          })) || [],
      })),
    [articles, getGatsbyImage, urlResolver]
  )

  return (
    <Box
      as="section"
      bgColor={mobileBackgroundColour ? { base: mobileBackgroundColour, md: bgColor } : bgColor}
      py={[9, 28]}
      overflowX="hidden"
      {...extraOverflowParentProps}
    >
      <Container>
        <HStack justifyContent={["center", "space-between"]} mb={[4, 8]} alignItems="center" spacing={4}>
          {title && (
            <Heading as="p" size="h2" textAlign={["center", "left"]} fontSize={["2xl", "8xl"]} lineHeight={1}>
              {title}
            </Heading>
          )}
          <CustomButton button={button} linkStyles={{ display: ["none", "block"] }} />
        </HStack>
        <CarouselOverflowWrapper type="articles">
          <Swiper
            modules={[Navigation]}
            navigation
            pagination={{ clickable: true }}
            slidesPerView="auto"
            centeredSlides
            breakpoints={{
              600: {
                centeredSlides: false,
              },
            }}
          >
            {items?.map((article: any) => (
              <SwiperSlide key={article?.key}>
                <Box maxW={80} w={80} bg={["brand.offWhite", "unset"]} p={[2, 0]} borderRadius="base" h="full">
                  <ArticleCard article={article} handleTrackingClick={handleTrackingClick} />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </CarouselOverflowWrapper>
        <CustomButton button={button} linkStyles={{ display: ["block", "none"], mt: 6 }} width="full" fontSize="xl" />
      </Container>
    </Box>
  )
}

export default memo(FeaturedArticles)
